import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";

import {
  AuthApi,
  AuthLoginPost200Response,
  CurrentUser,
  LoginRequest,
  MfaRequiredResponse,
  TokenResponse,
} from "@app/api/auth";
import { environment } from "@env/environment";
import { LoginModel } from "@models/login.model";
import { User } from "@models/user.model";
import {
  catchError,
  finalize,
  from,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  tap,
} from "rxjs";

/** Response of GET /api/auth/public-key. */
interface LoginPublicKey {
  publicKey: string;
  algorithm: string;
}

/** Response of POST /api/auth/2fa/authenticator/setup. */
export interface AuthenticatorSetup {
  /** True when the user already has an authenticator: challenge for a code instead of enrolling. */
  enrolled: boolean;
  /** Shared secret formatted for manual entry (present only when enrolling). */
  sharedKey?: string;
  /** otpauth:// URI to render as a QR code (present only when enrolling). */
  authenticatorUri?: string;
}

/** Response of POST /api/auth/2fa/authenticator/enable. */
interface EnableAuthenticatorResult {
  accessToken: string;
  tokenType: string;
  expiresIn: number;
  recoveryCodes: string[];
}

@Injectable({
  providedIn: "root",
})
export class AuthService {
  // The access token is kept in memory only. It is deliberately NOT persisted to
  // localStorage/sessionStorage: those are readable by any script on the origin, so an XSS could
  // exfiltrate them. The refresh token lives in an HttpOnly cookie the browser sends automatically.
  private accessToken: string | null = null;

  // De-duplicates concurrent refreshes (e.g. several requests 401 at once): the first call starts
  // the request, the rest share its result.
  private refreshInFlight$: Observable<TokenResponse> | null = null;

  constructor(
    private readonly authApi: AuthApi,
    private readonly http: HttpClient
  ) {}

  get authToken(): string | null {
    return this.accessToken;
  }

  get isAuthenticated(): boolean {
    return Boolean(this.accessToken);
  }

  login$(credentials: LoginModel): Observable<AuthLoginPost200Response> {
    // Encrypt the password with the server's RSA public key before it leaves the browser, so the
    // plaintext never appears in the request body. The key is fetched fresh for each attempt.
    return this.fetchPublicKey$().pipe(
      switchMap(
        (publicKey): Observable<string> =>
          from(this.encryptPassword(credentials.password, publicKey))
      ),
      switchMap((encryptedPassword): Observable<AuthLoginPost200Response> => {
        const request: LoginRequest = {
          username: credentials.email.trim().toLowerCase(),
          password: encryptedPassword,
        };

        return this.authApi.authLoginPost(request);
      }),
      tap((response): void => {
        if (this.isTokenResponse(response)) {
          this.storeAccessToken(response);
        }
      })
    );
  }

  /**
   * Exchanges the refresh-token cookie for a new access token. The cookie is sent automatically
   * (the Auth API client is configured with withCredentials); no token is passed from JS.
   */
  refresh$(): Observable<TokenResponse> {
    if (!this.refreshInFlight$) {
      this.refreshInFlight$ = this.authApi.authRefreshPost({}).pipe(
        tap((response): void => {
          this.storeAccessToken(response);
        }),
        finalize((): void => {
          this.refreshInFlight$ = null;
        }),
        shareReplay(1)
      );
    }

    return this.refreshInFlight$;
  }

  /**
   * Called once at application start (APP_INITIALIZER). Attempts a silent refresh so a valid
   * session survives a page reload; failure (no/expired cookie) is expected for anonymous users.
   */
  bootstrapSession(): Promise<void> {
    return new Promise<void>((resolve): void => {
      this.refresh$()
        .pipe(
          map((): void => undefined),
          catchError((): Observable<void> => of(undefined))
        )
        .subscribe({
          next: (): void => resolve(),
          error: (): void => resolve(),
        });
    });
  }

  getUserData$(): Observable<User> {
    return this.authApi.authMeGet().pipe(
      map((currentUser: CurrentUser): User => {
        return User.parseCurrentUser(currentUser);
      })
    );
  }

  /**
   * Starts authenticator (TOTP) enrollment for the pending login. Returns whether the user is
   * already enrolled (in which case we challenge for a code) or the shared secret + otpauth URI to
   * display as a QR code for first-time setup.
   */
  setup2fa$(mfaToken: string): Observable<AuthenticatorSetup> {
    return this.http.post<AuthenticatorSetup>(
      `${environment.AUTH_API_BASE_URL}/auth/2fa/authenticator/setup`,
      { mfaToken },
      { withCredentials: true }
    );
  }

  /**
   * Completes login by verifying the authenticator code against the pending MFA token. On success
   * the server returns the real access token (and sets the refresh cookie), which we store in memory.
   */
  verify2fa$(
    mfaToken: string,
    code: string,
    rememberDevice: boolean
  ): Observable<TokenResponse> {
    // Raw post (not the generated client) so we can pass rememberDevice, which the generated
    // request model does not include.
    return this.http
      .post<TokenResponse>(
        `${environment.AUTH_API_BASE_URL}/auth/2fa/verify`,
        { mfaToken, code, rememberDevice },
        { withCredentials: true }
      )
      .pipe(
        tap((response: TokenResponse): void => this.storeAccessToken(response))
      );
  }

  /**
   * Confirms first-time enrollment with the initial authenticator code. On success the server
   * enables 2FA, sets the refresh cookie, and returns the access token (stored in memory) plus the
   * one-time recovery codes to show the user once.
   */
  enable2fa$(
    mfaToken: string,
    code: string,
    rememberDevice: boolean
  ): Observable<EnableAuthenticatorResult> {
    return this.http
      .post<EnableAuthenticatorResult>(
        `${environment.AUTH_API_BASE_URL}/auth/2fa/authenticator/enable`,
        { mfaToken, code, rememberDevice },
        { withCredentials: true }
      )
      .pipe(tap((response): void => this.storeAccessToken(response)));
  }

  /** Completes login with a one-time recovery code instead of an authenticator code. */
  recovery2fa$(
    mfaToken: string,
    recoveryCode: string,
    rememberDevice: boolean
  ): Observable<TokenResponse> {
    return this.http
      .post<TokenResponse>(
        `${environment.AUTH_API_BASE_URL}/auth/2fa/recovery`,
        { mfaToken, recoveryCode, rememberDevice },
        { withCredentials: true }
      )
      .pipe(
        tap((response: TokenResponse): void => this.storeAccessToken(response))
      );
  }

  logout$(): Observable<void> {
    // The refresh-token cookie is sent automatically; no body needed.
    return this.authApi.authLogoutPost().pipe(
      tap((): void => {
        this.clearSession();
      }),
      map((): void => undefined)
    );
  }

  clearSession(): void {
    this.accessToken = null;
  }

  isMfaRequired(
    response: AuthLoginPost200Response
  ): response is MfaRequiredResponse {
    return "mfaRequired" in response && response.mfaRequired === true;
  }

  private storeAccessToken(response: { accessToken?: string | null }): void {
    const accessToken = response.accessToken;

    if (typeof accessToken !== "string" || accessToken.length === 0) {
      throw new Error("The authentication response contains an invalid token.");
    }

    this.accessToken = accessToken;
  }

  private isTokenResponse(
    response: AuthLoginPost200Response
  ): response is TokenResponse {
    return (
      "accessToken" in response &&
      typeof response.accessToken === "string" &&
      response.accessToken.length > 0
    );
  }

  private fetchPublicKey$(): Observable<string> {
    return this.http
      .get<LoginPublicKey>(`${environment.AUTH_API_BASE_URL}/auth/public-key`)
      .pipe(map((response): string => response.publicKey));
  }

  private async encryptPassword(
    password: string,
    publicKeyBase64: string
  ): Promise<string> {
    const key = await crypto.subtle.importKey(
      "spki",
      this.base64ToBytes(publicKeyBase64),
      { name: "RSA-OAEP", hash: "SHA-256" },
      false,
      ["encrypt"]
    );

    const ciphertext = await crypto.subtle.encrypt(
      { name: "RSA-OAEP" },
      key,
      new TextEncoder().encode(password)
    );

    return this.bytesToBase64(new Uint8Array(ciphertext));
  }

  // TypeScript 5.9 made Uint8Array generic over its buffer. WebCrypto's
  // BufferSource only accepts an ArrayBuffer-backed view, so the narrower
  // type has to be preserved here rather than widened to ArrayBufferLike.
  private base64ToBytes(base64: string): Uint8Array<ArrayBuffer> {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }

  private bytesToBase64(bytes: Uint8Array): string {
    let binary = "";
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }
}
